import * as React from 'react';
import { Animation, Relative } from './JRCStepper.style';

/**
 * @member activeStep active step of the stepper. Starts at 0.
 * @member children iterable of the steps (!!)
 */
export type JRCFadeStepperProps = {
    activeStep: number;
    children: React.ReactNode | Iterable<React.ReactNode>;
};

type CurrentStepsType = {
    steps: [number | undefined, number | undefined];
    active: undefined | 0 | 1;
};

// Steps contains the current and previous steps during animations
// and only the current step when animation is done.
// active is a pointer to the element in the steps array that is
// the current step at any time.
// Using this structure allows to dismount the current step only
// when it ends its outing transition animation.
const initCurrentSteps: CurrentStepsType = {
    steps: [undefined, undefined],
    active: undefined,
};

export function JRCFadeStepper(props: JRCFadeStepperProps) {
    const steps = React.Children.toArray(props.children);

    const [animationRunning, setAnimationRunning] = React.useState(false);
    const [currentSteps, setCurrentSteps] = React.useState<CurrentStepsType>(initCurrentSteps);
    const relativeRef = React.useRef<HTMLDivElement>(null);
    const lastDirection = React.useRef<string | undefined>(undefined);

    const getDirection = React.useCallback((stepsState: CurrentStepsType) => {
        if (stepsState.active === undefined || stepsState.steps[0] === undefined || stepsState.steps[1] === undefined) {
            return lastDirection.current;
        }

        const oldStep = stepsState.active === 0 ? stepsState.steps[1] : stepsState.steps[0];
        const currentStep = stepsState.active === 0 ? stepsState.steps[0] : stepsState.steps[1];

        if (currentStep < oldStep) {
            lastDirection.current = 'previous';
            return 'previous';
        }

        if (currentStep >= oldStep) {
            lastDirection.current = 'next';
            return 'next';
        }

        return lastDirection.current;
    }, []);

    React.useEffect(() => {
        setCurrentSteps((current) => {
            const newIndex =
                props.activeStep < 0 ? 0 : props.activeStep >= steps.length ? steps.length - 1 : props.activeStep;
            // Active is not set, this is the first render
            if (current.active === undefined) {
                return {
                    ...current,
                    active: 0,
                    steps: [newIndex, undefined],
                };
            }

            // new step is the same as current, do not change anything
            if (newIndex === current.steps[current.active]) {
                return current;
            }

            // animation will start as we are cahnging step
            setAnimationRunning(true);

            // active changes (0->1 or 1->0, newIndex goes to the new active position)
            return {
                ...current,
                active: current.active === 0 ? 1 : 0,
                steps: [
                    current.active === 0 ? current.steps[0] : newIndex,
                    current.active === 1 ? current.steps[1] : newIndex,
                ],
            };
        });
    }, [props.activeStep, steps.length]);

    React.useEffect(() => {
        let timeoutId: number | undefined = undefined;
        if (animationRunning) {
            // Prepare a timeout to set to undefined oldstep once animation ends to dismount it.
            timeoutId = window.setTimeout(() => {
                setAnimationRunning(false);
                setCurrentSteps((current) => {
                    return {
                        ...current,
                        steps: [
                            current.active === 0 ? current.steps[0] : undefined,
                            current.active === 1 ? current.steps[1] : undefined,
                        ],
                    };
                });
            }, 1000);
        }
        return () => {
            timeoutId !== undefined && clearTimeout(timeoutId);
        };
    }, [animationRunning]);

    if (currentSteps.active === undefined) return null;

    const step0 = currentSteps.steps[0] ?? 0;
    const step1 = currentSteps.steps[1] ?? 0;

    return (
        <>
            <Relative ref={relativeRef}>
                {currentSteps.steps[0] !== undefined && (
                    <Animation
                        animation={
                            getDirection(currentSteps)
                                ? `${currentSteps.active === 0 ? 'intro' : 'outro'}-${getDirection(currentSteps)}`
                                : 'start'
                        }>
                        <div>{steps[step0]}</div>
                    </Animation>
                )}

                {currentSteps.steps[1] !== undefined && (
                    <Animation
                        animation={
                            getDirection(currentSteps)
                                ? `${currentSteps.active === 1 ? 'intro' : 'outro'}-${getDirection(currentSteps)}`
                                : currentSteps.active === 1
                                ? 'start'
                                : undefined
                        }>
                        <div>{steps[step1]}</div>
                    </Animation>
                )}
            </Relative>
        </>
    );
}
